import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import DeleteModal from "components/DeleteModal/DeleteModal";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { adminGetAllClients, deleteUser } from "redux/features/auth/authSlice";
import Input from "shared/Input/Input";
import DashboardMenu from "../../DashboardPage/DashboardMenu";
import ClientDetailsPage from "./ClientDetailsPage";
import ValidateClientModal from "./ValidateClientModal";
import { Checkbox } from "@material-tailwind/react";
import DynamicTable from "components/Tables/DynamicTable";
import { FaUsers } from "react-icons/fa";
import Loading from "components/Loader/Loading";

export interface ListClientsPageProps {
  className?: string;
}

const ListClientsPage: FC<ListClientsPageProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const {
    clients: clientList,
    totalCount,
    totalPages,
    currentPage,
    loading,
    error,
  } = useAppSelector(
    (state) =>
      state?.auth?.allClients || {
        clients: [],
        totalCount: 0,
        totalPages: 0,
        currentPage: 1,
        loading: "idle",
      }
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      await dispatch(adminGetAllClients({ page, searchTerm: debouncedSearchTerm }));
      setIsSearchLoading(false);
    };
    fetchData();
  }, [dispatch, page, debouncedSearchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [validateModalOpen, setValidateModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const [showClientDetails, setShowClientDetails] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteModalOpen = (client: any) => {
    setSelectedClient(client);
    setShowClientDetails(false);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedClient && selectedClient.User?.id !== undefined) {
      setDeleting(true);
      await dispatch(deleteUser(selectedClient.User.id));
      setDeleting(false);
      setDeleteModalOpen(false);
      await dispatch(adminGetAllClients({ page, searchTerm: debouncedSearchTerm }));
    }
  };

  const handleEyeIconClick = (client: any) => {
    setSelectedClient(client);
    setShowClientDetails(true);
    setDeleteModalOpen(false);
  };

  const handleValidateModalOpen = (client: any) => {
    setSelectedClient(client);
    setValidateModalOpen(true);
  };

  const columns = [
    {
      key: "name",
      label: "Nom et prénom",
      render: (item: any) => <span>{`${item.lastName} ${item.name}`}</span>,
    },
    {
      key: "accountType",
      label: "Type de compte",
      render: (item: any) => {
        if (item.validation === "") return <span>Client</span>;
        if (item.validation === "en cours") return <span>Demande en cours</span>;
        if (item.validation === "bloqué") return <span>Demande bloquée</span>;
        return "-";
      },
    },
    {
      key: "host",
      label: "Devenir hôte",
      render: (item: any) => <Checkbox defaultChecked={item?.User?.isOwner} disabled />,
    },
    {
      key: "host",
      label: "Validate",
      render: (item: any) => (
        <div className="flex space-x-3">
          <i
            className="text-2xl text-blue-500 cursor-pointer las la-edit hover:text-blue-700"
            onClick={() => handleValidateModalOpen(item)}
            aria-label={`Validate ${item.lastName} ${item.name}`}
            title="Valider"
          ></i>
        </div>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (item: any) => (
        <div className="flex space-x-3">
          <i
            className="text-2xl text-blue-500 cursor-pointer las la-eye hover:text-blue-700"
            onClick={() => handleEyeIconClick(item)}
            aria-label={`View ${item.lastName} ${item.name}`}
            title="Consulter"
          ></i>
          <i
            className="text-2xl text-red-500 cursor-pointer las la-trash-alt hover:text-red-700"
            onClick={() => handleDeleteModalOpen(item)}
            aria-label={`Delete ${item.lastName} ${item.name}`}
            title="Supprimer"
          ></i>
        </div>
      ),
    },
  ];

  const handleTableAction = (action: string, item: any) => {
    if (action === "view") {
      handleEyeIconClick(item);
    } else if (action === "delete") {
      handleDeleteModalOpen(item);
    } else if (action === "validate") {
      handleValidateModalOpen(item);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center space-x-3">
              <FaUsers className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Liste Des Clients
              </h2>
            </div>
            <div className="relative w-full max-w-lg">
              <Input
                type="text"
                placeholder="Rechercher par nom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                aria-label="Search clients"
              />
              <MagnifyingGlassIcon
                className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                  searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                }`}
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                  aria-label="Clear search"
                >
                  <XMarkIcon />
                </button>
              )}
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {loading === "pending" && !isSearchLoading && <Loading />}

          {error && (
            <div className="py-4 text-center text-red-600 dark:text-red-400">
              Error: {error.message || "Failed to load clients"}
            </div>
          )}

          {loading !== "pending" && !error && (
            <>
              {deleteModalOpen && (
                <DeleteModal
                  open={deleteModalOpen}
                  handleClose={() => setDeleteModalOpen(false)}
                  handleDelete={handleDeleteConfirm}
                  category={{
                    id: selectedClient?.User?.id || 0,
                    designation: `${selectedClient?.lastName} ${selectedClient?.name}` || "",
                  }}
                />
              )}

              {validateModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div
                    className="fixed inset-0 bg-black opacity-50"
                    onClick={() => setValidateModalOpen(false)}
                  ></div>
                  <div className="relative w-full max-w-lg p-6 bg-white rounded-lg dark:bg-neutral-900">
                    <ValidateClientModal
                      client={selectedClient}
                      onClose={() => setValidateModalOpen(false)}
                      onValidate={() => {
                        setValidateModalOpen(false);
                        dispatch(adminGetAllClients({ page, searchTerm: debouncedSearchTerm }));
                      }}
                    />
                  </div>
                </div>
              )}

              <DynamicTable
                columns={columns}
                data={clientList}
                totalItems={totalCount || 0}
                currentPage={currentPage || 1}
                onPageChange={handlePageChange}
                onAction={handleTableAction}
                totalPages={totalPages}
                availableActions={["view", "delete"]}
              />

              {showClientDetails && !deleting && (
                <ClientDetailsPage
                  open={true}
                  handleClose={() => setShowClientDetails(false)}
                  clientData={selectedClient}
                />
              )}
            </>
          )}
        </div>
      </DashboardMenu>
    </div>
  );
};

export default ListClientsPage;
